import { IconButton, TextField } from "@flat-ui";
import { InputAdornment, useTheme } from "@mui-wrapper";
import { UlmInputLabel } from "@components/ulm/ulm-input-label";
import { ReactNode, useState } from "react";
import VisibilityOnIcon from "@assets/icons/visibility-on_24px.svg?react";
import VisibilityOffIcon from "@assets/icons/visibility-off_24px.svg?react";
import { fieldSpacingSx } from "@styles/common-styles";

export interface Props {
  /** Input value */
  value: string;

  /** Whether to show/hide the label */
  shouldShowLabel?: boolean;

  /** True if the field has error to be displayed */
  hasError?: boolean;

  /** Message shown below the field while `hasError` is set */
  helperText?: ReactNode;

  /** Optional component to show on the right side of the label */
  helpComponent?: React.ReactNode;

  /** True if the text field should be disabled */
  isDisabled?: boolean;

  /** Set the input name label */
  label?: string;

  /** Text to show the user inside the input */
  placeholder?: string;

  /** Function that triggers on every input value change */
  onChange?(event: React.ChangeEvent<HTMLInputElement>): void;

  /** Function that triggers on every key event */
  onKeyUp?(event: React.KeyboardEvent<HTMLInputElement>): void;

  /** Function that triggers when the input gains focus */
  onFocus?(event: React.FocusEvent<HTMLInputElement>): void;

  /** Function that triggers when the input loses focus */
  onBlur?(event: React.FocusEvent<HTMLInputElement>): void;
}

/** Renders the password field */
export function UlmPasswordField({
  value,
  hasError,
  helperText,
  shouldShowLabel = true,
  helpComponent,
  placeholder = "Enter your password",
  isDisabled = false,
  label = "Password",
  onChange,
  onKeyUp,
  onFocus,
  onBlur,
}: Props): JSX.Element {
  const [shouldShowPassword, setShouldShowPassword] = useState<boolean>(false);
  // `color` is a CSS colour rather than a palette path, so pass the scheme-following variable
  const { vars } = useTheme();

  return (
    <>
      {/* The label stays local: it carries `helpComponent` on its right side,
          which flat-ui's own label has no slot for. */}
      {shouldShowLabel && (
        <UlmInputLabel title={label} helpComponent={helpComponent} />
      )}

      <TextField
        fullWidth
        text={value}
        type={shouldShowPassword ? "text" : "password"}
        placeholder={placeholder}
        disabled={isDisabled}
        // The call sites only ever pass `helperText` as the error message, and
        // always in lockstep with `hasError`. flat-ui takes the two as one prop.
        error={hasError ? (helperText ?? " ") : undefined}
        onFocus={onFocus}
        onBlur={onBlur}
        controlStyle={fieldSpacingSx}
        // See UlmTextField: `InputProps` is spread onto the input last, so the
        // call sites keep the real event instead of flat-ui's `onTextChanged`.
        InputProps={{
          name: "password",
          autoComplete: "password",
          onChange,
          onKeyUp,
        }}
        endAdornment={
          <InputAdornment position="end">
            <IconButton
              onClick={() => setShouldShowPassword(!shouldShowPassword)}
              disableRipple
              color={vars.palette.text.secondary}
              hoverColor={vars.palette.text.secondary}
            >
              {shouldShowPassword ? (
                <VisibilityOnIcon />
              ) : (
                <VisibilityOffIcon />
              )}
            </IconButton>
          </InputAdornment>
        }
      />
    </>
  );
}
